import moment from "moment"
import { markAsTimeOnly } from "@/helper/time-helper"

export default class DataHelper {
  public static convertDateStringsToMoment(body: any) {
    // Check if the body is null, undefined, or not an object
    if (body === null || body === undefined || typeof body !== "object") return body

    for (const key of Object.keys(body)) {
      const value = body[key]

      // Check if the property is an ISO date string
      if (this.isIsoDateString(value)) {
        body[key] = moment(value, "YYYY-MM-DD")
      }
      // Check if the property is an ISO date-time string
      else if (this.isIsoDateTimeString(value)) {
        body[key] = moment(value)
      }
      // Check if the property is a time-only string (HH:mm:ss or HH:mm)
      else if (this.isTimeString(value)) {
        body[key] = markAsTimeOnly(moment(value, "HH:mm:ss"))
      }
      // Check if the property is an object, then recursively process the object
      else if (typeof value === "object" && !Array.isArray(value)) {
        body[key] = this.convertDateStringsToMoment(value)
      }
      // Check if the property is an array
      else if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          // Process each item of the array, could be a nested array or an object or a date string
          if (typeof value[i] === "string") {
            if (this.isIsoDateString(value[i])) {
              value[i] = moment(value[i], "YYYY-MM-DD")
            } else if (this.isIsoDateTimeString(value[i])) {
              value[i] = moment(value[i])
            } else if (this.isTimeString(value[i])) {
              value[i] = markAsTimeOnly(moment(value[i], "HH:mm:ss"))
            }
          } else if (typeof value[i] === "object") {
            value[i] = this.convertDateStringsToMoment(value[i])
          }
        }
      }
    }
    return body
  }

  public static convertMomentToDateStrings(body: any) {
    if (body === null || body === undefined || typeof body !== "object") return body

    for (const key of Object.keys(body)) {
      const value = body[key]
      if (moment.isMoment(value)) {
        if ((value as any).__timeOnly) {
          // Time-only format for Django TimeField
          body[key] = value.format("HH:mm:ss")
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        else if (value._f === "YYYY-MM-DD") {
          // Only date portion of ISO format
          body[key] = value.format("YYYY-MM-DD")
        } else {
          // Full datetime format
          body[key] = value.format("YYYY-MM-DD[T]HH:mm:ss.SSS")
        }
      } else if (typeof value === "object") body[key] = this.convertMomentToDateStrings(value)
    }
    return body
  }

  private static isIsoDateTimeString(value: any): boolean {
    const isoDateFormat = /^\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d(\.\d+)?$/
    return value && typeof value === "string" && isoDateFormat.test(value)
  }

  private static isIsoDateString(value: any): boolean {
    const isoDateFormat = /^\d{4}-[01]\d-[0-3]\d$/
    return value && typeof value === "string" && isoDateFormat.test(value)
  }

  /**
   * Check if the value is a time-only string in the format HH:mm:ss or HH:mm.
   * This matches Django's TimeField output format.
   */
  private static isTimeString(value: any): boolean {
    const timeFormat = /^[0-2]\d:[0-5]\d(:[0-5]\d(\.\d+)?)?$/
    return value && typeof value === "string" && timeFormat.test(value)
  }

  /**
   * Denormalize the response data by replacing all objects with a sid reference.
   * @param data The response data structure to denormalize
   */
  public static denormalizeData(data: any) {
    // List responses are now plain nested data (the {structure, objects} normalization was
    // removed). Return them untouched. Relations that are uuid strings are resolved lazily by
    // the models via ModelManager; nested objects are already models. The legacy branch below
    // stays only to tolerate any in-flight {structure, objects} payload during a deploy.
    if (data === null || typeof data !== "object" || !("structure" in data)) {
      return data
    }

    const { structure, objects } = data
    this.traverse(structure, objects)
    return structure
  }

  /**
   * Recursively traverse the data and replace all sid references with the actual object.
   * Nested objects without a sid property are not replaced. Think Coords, Battery, MotionActivity.
   * @param data The normalized data structure containing sid references
   * @param objects The objects that are referenced by sid
   * @private This method is only used internally by the denormalizeData method
   */
  private static traverse(data: any, objects: { [key: string]: any }) {
    if (Array.isArray(data)) {
      for (const elem of data) {
        this.traverse(elem, objects)
      }
    } else if (typeof data === "object" && data !== null) {
      for (const key of Object.keys(data)) {
        if (typeof data[key] === "object") {
          this.traverse(data[key], objects)
        }
      }
      // TODO replace with data.hasOwn()
      // Only replace if sid exists in objects lookup to avoid replacing normal objects
      // that happen to have a sid property but are not meant to be denormalized
      if (Object.prototype.hasOwnProperty.call(data, "sid") && data["sid"] in objects) {
        Object.assign(data, objects[data["sid"]])
      }
    }
  }
}
